import {
  AppShell,
  AppShellBody,
  AppShellRailButton,
  AppShellTop,
  AppShellTopActions,
  Avatar,
  Button,
  ButtonIcon,
  cn,
  ContextSwitcher,
  GlobalSearch,
  GlobalSearchInput,
  GlobalSearchShortcut,
  Kbd,
  ShellIconHelpCircle,
  ShellIconLayers,
  ShellIconMenu,
  ShellIconSearch,
  useBreakpoint,
} from '@zrsystem/ui';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import {
  AppShellNavSidebar,
  readAppShellSidebarExpanded,
  resolveAppShellSidebarWidth,
} from '@/app/layout/app-shell-nav-sidebar.js';
import { formatPlaceContextLabel, getUserInitials } from '@/app/layout/app-shell-utils.js';
import { ShellAccountMenu } from '@/app/layout/shell-account-menu.js';
import { filterNavEntriesByRoles } from '@/app/navigation-context/filter-nav-entries-by-roles.js';
import { filterNavEntriesForPersonKind } from '@/app/navigation-context/filter-nav-entries-for-person-kind.js';
import { resolveNavEntries } from '@/app/navigation-context/resolve-nav-entries.js';
import { useNavigationContext } from '@/app/navigation-context/use-navigation-context.js';
import { useAuth } from '@/app/providers/auth-provider.js';
import { appPaths } from '@/app/router/paths.js';
import { useCommandPaletteContext } from '@/app/surfaces/command-palette-provider.js';
import { CommandPalette } from '@/app/surfaces/command-palette.js';
import { FormDrawerHost } from '@/app/surfaces/form-drawer-host.js';
import { QuickPanelDock } from '@/app/surfaces/quick-panel-dock.js';
import { QuickPanelHost } from '@/app/surfaces/quick-panel-host.js';
import { SurfaceShortcuts } from '@/app/surfaces/surface-shortcuts.js';
import {
  ChatSidePanelProvider,
  useChatSidePanel,
} from '@/features/conversation/chat-side-panel-context.js';
import { ChatSidePanel } from '@/features/conversation/components/chat-side-panel.js';
import { WhatsAppInboxHeaderBadge } from '@/features/notification/components/whatsapp-inbox-header-badge.js';
import { NotificationBell } from '@/features/notifications/components/notification-bell.js';
import { useAckStaffCheckoutNotifications } from '@/features/notifications/hooks/use-ack-staff-checkout-notifications.js';
import { IndividualOrganizationCompletionBanner } from '@/features/platform/components/individual-organization-completion-banner.js';
import {
  isDesignSystemPreviewEnabled,
  isDesignSystemPreviewPath,
} from '@/shared/config/design-system-preview.js';
import { installUserCommitTurnListener } from '@/shared/feedback/user-commit-turn.js';
import { SHELL_SIDEBAR_EXPANDED_STORAGE_KEY } from '@/shared/shell/nav.types.js';
import { ShellBottomNav } from '@/shared/shell/shell-bottom-nav.js';

export function AuthenticatedLayout() {
  return (
    <ChatSidePanelProvider>
      <AuthenticatedShell />
    </ChatSidePanelProvider>
  );
}

function AuthenticatedShell() {
  const navigate = useNavigate();
  const { openChannel, close } = useChatSidePanel();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const {
    level,
    contextLabel,
    isStaticSwitcher,
    switcherSections,
    setLevel,
    allowedPlaces,
    activeEntityId,
  } = useNavigationContext();
  const { openCommandPalette } = useCommandPaletteContext();
  const breakpoint = useBreakpoint();
  const isPhone = breakpoint === 'phone';
  const isTablet = breakpoint === 'tablet';
  const [sidebarExpanded, setSidebarExpanded] = useState(readAppShellSidebarExpanded);
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  // No phone a sidebar vira drawer sobreposto (aberto pelo gatilho do header / bottom-nav);
  // no tablet colapsa como rail de ícones; no desktop segue a preferência do usuário.
  const sidebarExpandedEffective = isPhone ? true : isTablet ? false : sidebarExpanded;

  const navEntries = useMemo(() => {
    const entries = filterNavEntriesByRoles(resolveNavEntries(level), user?.roles ?? []);
    return filterNavEntriesForPersonKind(entries, user?.personKind, level);
  }, [level, user?.personKind, user?.roles]);
  const isPlaceLevel = level === 'place';
  // Tenant includes Financeiro (AP/AR) which stacks QP from page drawers (ex.: + fornecedor).
  const showQuickPanelChrome = isPlaceLevel || level === 'tenant' || level === 'platform';
  const userInitials = getUserInitials(user);
  const designSystemEnabled = isDesignSystemPreviewEnabled();
  const designSystemActive = isDesignSystemPreviewPath(location.pathname);
  const checkoutDetailMatch = /^\/check-out\/([0-9a-f-]{36})$/i.exec(location.pathname);
  useAckStaffCheckoutNotifications(checkoutDetailMatch?.[1]);

  const activePlaceLabel = useMemo(() => {
    if (!isPlaceLevel) {
      return contextLabel;
    }
    const place = allowedPlaces.find((item) => item.id === activeEntityId);
    return place ? formatPlaceContextLabel(place) : contextLabel;
  }, [activeEntityId, allowedPlaces, contextLabel, isPlaceLevel]);

  useEffect(() => installUserCommitTurnListener(), []);

  useEffect(() => {
    window.localStorage.setItem(
      SHELL_SIDEBAR_EXPANDED_STORAGE_KEY,
      sidebarExpanded ? 'true' : 'false',
    );
  }, [sidebarExpanded]);

  useEffect(() => {
    setNavDrawerOpen(false);
  }, [location.pathname]);

  const shellStyle = {
    '--shell-sidebar-width': resolveAppShellSidebarWidth(sidebarExpandedEffective),
  } as CSSProperties;

  return (
    <>
      <SurfaceShortcuts />

      <AppShell
        flat
        className={cn(
          isPhone && 'shell--phone',
          isTablet && 'shell--tablet',
          isPhone && navDrawerOpen && 'shell--nav-drawer-open',
        )}
        style={shellStyle}
      >
        {isPhone && navDrawerOpen ? (
          <button
            type="button"
            className="shell-nav-drawer-backdrop"
            aria-label="Fechar menu"
            onClick={() => {
              setNavDrawerOpen(false);
            }}
          />
        ) : null}

        <AppShellNavSidebar
          entries={navEntries}
          expanded={sidebarExpandedEffective}
          onExpandedChange={setSidebarExpanded}
          footerExtras={
            <>
              {designSystemEnabled ? (
                <AppShellRailButton
                  type="button"
                  aria-label="Design system"
                  aria-current={designSystemActive ? 'page' : undefined}
                  active={designSystemActive}
                  title="Inventário visual — composição de telas"
                  onClick={() => {
                    void navigate(appPaths.harnessDesignSystem);
                  }}
                >
                  <ShellIconLayers size={16} />
                </AppShellRailButton>
              ) : null}
              <AppShellRailButton
                type="button"
                aria-label="Conta"
                title={user?.email ?? 'Conta'}
                disabled
              >
                <Avatar size="sm">{userInitials}</Avatar>
              </AppShellRailButton>
            </>
          }
        />

        <AppShellTop>
          {isPhone ? (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              className="shell-top-menu-trigger zr-touch-target"
              aria-label={navDrawerOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={navDrawerOpen}
              onClick={() => {
                setNavDrawerOpen((open) => !open);
              }}
            >
              <ButtonIcon>
                <ShellIconMenu size={18} />
              </ButtonIcon>
            </Button>
          ) : null}

          <ContextSwitcher
            sections={switcherSections}
            contextLabel={activePlaceLabel}
            isStatic={isStaticSwitcher}
            onSelect={(nextLevel, entityId) => {
              void setLevel(nextLevel, entityId);
            }}
          />

          {isPlaceLevel && !isPhone ? (
            <GlobalSearch
              role="button"
              tabIndex={0}
              aria-label="Abrir paleta de comandos"
              onClick={() => {
                openCommandPalette();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openCommandPalette();
                }
              }}
            >
              <ShellIconSearch size={13} />
              <GlobalSearchInput
                placeholder="Buscar OS, cliente, veículo…"
                readOnly
                aria-hidden
                tabIndex={-1}
              />
              <GlobalSearchShortcut>
                <Kbd>⌘K</Kbd>
              </GlobalSearchShortcut>
            </GlobalSearch>
          ) : (
            <div style={{ flex: 1, minWidth: 0 }} />
          )}

          <AppShellTopActions>
            {isPlaceLevel && isPhone ? (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                className="zr-touch-target"
                aria-label="Abrir paleta de comandos"
                onClick={() => {
                  openCommandPalette();
                }}
              >
                <ButtonIcon>
                  <ShellIconSearch size={18} />
                </ButtonIcon>
              </Button>
            ) : null}
            {isPlaceLevel ? <WhatsAppInboxHeaderBadge /> : null}
            <NotificationBell
              onNavigate={(path) => {
                if (path === appPaths.conversations) {
                  openChannel('workshop');
                  return;
                }
                void navigate(path);
              }}
              conversationsPath={appPaths.conversations}
              checkInResumePath={appPaths.serviceOrderCheckInResume}
              checkOutDetailPath={appPaths.serviceOrderCheckOutDetail}
              serviceOrderDetailPath={appPaths.serviceOrderDetail}
              quoteDetailPath={appPaths.serviceOrderQuoteDetail}
              workTimerOvertimePath={(mechanicUserId) =>
                appPaths.workTimerOvertimeConfirm(mechanicUserId)
              }
            />
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Ajuda"
              disabled
              title="Ajuda (em breve)"
            >
              <ButtonIcon>
                <ShellIconHelpCircle size={18} />
              </ButtonIcon>
            </Button>
            {user ? (
              <ShellAccountMenu email={user.email} initials={userInitials} onSignOut={signOut} />
            ) : null}
          </AppShellTopActions>
        </AppShellTop>

        <AppShellBody>
          <IndividualOrganizationCompletionBanner />
          <Outlet />
        </AppShellBody>

        {isPhone ? (
          <ShellBottomNav
            entries={navEntries}
            pathname={location.pathname}
            onNavigate={(path) => {
              void navigate(path);
            }}
            onOpenMenu={() => {
              setNavDrawerOpen(true);
            }}
          />
        ) : null}
      </AppShell>

      {showQuickPanelChrome ? (
        <>
          <FormDrawerHost />
          <QuickPanelHost />
          <QuickPanelDock />
          {isPlaceLevel ? <CommandPalette /> : null}
        </>
      ) : null}

      {isPlaceLevel ? (
        <ChatSidePanel
          onNavigateToSettings={() => {
            close();
            void navigate(appPaths.placeSettings);
          }}
        />
      ) : null}

      <Toaster position="top-center" offset={{ top: 56 }} richColors dir="ltr" duration={4000} />
    </>
  );
}
